import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, FileDown } from "lucide-react";
import { toast } from "sonner";

const canDownloadSanctioned = (role) => ["Admin", "Accounts"].includes(role);
const canDownloadBlank = (role) => role === "Admin";
const canDownloadWorking = (role) => ["Admin", "Accounts"].includes(role);

/**
 * Three template downloads for Budget V2 (RKF standard Excel format).
 * Each hits the backend, which prefills the blank template with live data
 * and streams the .xlsx back. Access is 403 for anyone other than Admin
 * (Super Admin) or Accounts — hidden from the UI for other roles.
 */
export default function BudgetTemplateDownloads({ projectId, projectCode, userRole }) {
  const [busy, setBusy] = useState(null);
  if (!canDownloadSanctioned(userRole) && !canDownloadWorking(userRole)) return null;

  const download = async (path, label, filename) => {
    if (!projectId) return;
    setBusy(path);
    try {
      const r = await api.get(`${path}?project_id=${projectId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `${projectCode}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} downloaded`);
    } catch (e) {
      const msg = e.response?.status === 403 ? "You don't have permission to download this template" :
                  (e.response?.data?.detail || e.message);
      toast.error(msg);
    }
    setBusy(null);
  };

  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card data-testid="v2-template-downloads">
      <CardContent className="p-3 flex flex-wrap gap-2 items-center">
        <span className="text-xs font-medium text-slate-500 mr-2">RKF Excel templates:</span>
        {canDownloadBlank(userRole) && (
          <Button size="sm" variant="outline" disabled={!projectId || !!busy}
            onClick={() => download("/v2/budgets/template/sanctioned-blank", "Sanctioned template (blank)", `${projectCode}_Sanctioned_Blank_${today}.xlsx`)}
            data-testid="v2-dl-sanc-blank">
            <FileDown className="w-3.5 h-3.5 mr-1" /> Sanctioned template (blank)
          </Button>
        )}
        {canDownloadSanctioned(userRole) && (
          <Button size="sm" variant="outline" disabled={!projectId || !!busy}
            onClick={() => download("/v2/budgets/template/sanctioned-current", "Current sanctioned", `${projectCode}_Sanctioned_${today}.xlsx`)}
            data-testid="v2-dl-sanc-current">
            <Download className="w-3.5 h-3.5 mr-1" /> Download current Sanctioned
          </Button>
        )}
        {canDownloadWorking(userRole) && (
          <Button size="sm" variant="outline" disabled={!projectId || !!busy}
            onClick={() => download("/v2/budgets/template/working", "Working template", `${projectCode}_Working_${today}.xlsx`)}
            data-testid="v2-dl-working">
            <Download className="w-3.5 h-3.5 mr-1" /> Download Working template
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
